import React from 'react';
import {clsx} from 'clsx';
import {Typography} from 'onboarding-components';
import {BrowserRouter, Link} from 'react-router-dom';

import {useGlobalSidenavController} from 'common/contexts/react/global-sidenav-controller-context';
import {Trans, TranslationKey} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {getProjectId} from 'common/report/util';
import {SvgIconReact} from 'common/components/svg-icon';
import {useProjectTallies} from 'iron/common/onboarding/immersive-onboarding/utils';
import {
  TrackingEvent,
  TrackingProperty,
  trackOnboardingEvent,
} from 'iron/common/onboarding/immersive-onboarding/tracking';
import {SetupGuideContext} from 'common/onboarding/immersive-onboarding/types';
import {calculatePollingInterval} from 'iron/common/onboarding/immersive-onboarding/utils';
import {CalloutBlock} from 'iron/common/onboarding/immersive-onboarding/components/callout-block';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import common from 'iron/common/onboarding/immersive-onboarding/steps/common.module.styl';
import {Step} from 'iron/common/onboarding/immersive-onboarding/types';
import {isSessionRecordingHomeEnabled} from 'common/report/util/feature-sampling';

import ICONS from './icons';
import tallyStyle from './icons/icon.module.styl';
import boxStyle from './event-status-box.module.styl';
import rippleStyle from './icons/ripple.module.styl';

enum TallyType {
  Events = `events`,
  Replays = `replays`,
  Users = `users`,
}

interface TallyIndicatorProps {
  icon?: TallyType;
  loaded: boolean;
  timedOut: boolean;
  label: TranslationKey;
}

interface VerifiedBoxProps {
  className?: string;
}

interface StatusTextConfig {
  heading: string;
  description?: string;
  docLink?: React.JSX.Element;
}

const tallyConfig: Record<TallyType, {translationKey: string; iconName: TallyType}> = {
  users: {
    translationKey: `onboarding.verify.status-box.tallies.users`,
    iconName: TallyType.Users,
  },
  events: {
    translationKey: `onboarding.verify.status-box.tallies.events`,
    iconName: TallyType.Events,
  },
  replays: {
    translationKey: `onboarding.verify.status-box.tallies.replays`,
    iconName: TallyType.Replays,
  },
};

const {intervalMs, totalAttempts} = calculatePollingInterval({intervalSeconds: 30, totalMinutes: 2});

const TallyIndicator: React.FC<TallyIndicatorProps> = ({icon, label, loaded, timedOut}) => {
  const {t} = useTranslation();
  const currentProject = useCurrentProject().get().value;
  const globalSidenavController = useGlobalSidenavController();
  const {modalManager} = React.useContext(SetupGuideContext);

  const {closeModal} = modalManager;

  const URL_MAP: Record<TallyType, string> = {
    [TallyType.Users]: `users`,
    [TallyType.Events]: `events`,
    [TallyType.Replays]: isSessionRecordingHomeEnabled(currentProject) ? `session-replay` : `home`,
  };

  const handleTallyClick = (source: string) => {
    trackOnboardingEvent(TrackingEvent.TallyIconClicked, {
      [TrackingProperty.CurrentStep]: Step.Verify,
      [TrackingProperty.TallyIndicator]: source,
    });

    closeModal();
  };

  return (
    <BrowserRouter>
      <Link
        to={globalSidenavController.getWorkspaceBasedAppUrl(URL_MAP[icon as TallyType])}
        onClick={() => handleTallyClick(URL_MAP[icon as TallyType])}
        className={clsx(boxStyle.boxIconContainer, common.mb8, {[boxStyle.loaded]: loaded})}
      >
        {icon &&
          React.createElement(ICONS[icon], {
            className: clsx({[tallyStyle.loaded]: loaded, [tallyStyle.timedOut]: timedOut}),
          })}
        <Typography variant='captionXs' weight='bold'>
          {t(label)}
        </Typography>
      </Link>
    </BrowserRouter>
  );
};

export const VerifiedBox: React.FC<VerifiedBoxProps> = ({className}: {className?: string}) => {
  const {t} = useTranslation();
  const projectId = parseInt(getProjectId(), 10);
  const {talliesStatus, isLoading, hasError, hasTimedOut, refetch} = useProjectTallies(projectId, {
    pollUntilData: true,
    pollInterval: intervalMs,
    maxPollAttempts: totalAttempts,
  });

  const {useAutocapture, useSessionReplay} = React.useContext(SetupGuideContext);

  const [isIdling, setIsIdling] = React.useState(false);

  const tallyStatus: Record<TallyType, boolean> = React.useMemo(() => {
    if (!talliesStatus) {
      return {
        events: false,
        users: false,
        replays: false,
      };
    }

    return {
      [TallyType.Events]: talliesStatus.events,
      [TallyType.Users]: talliesStatus.users,
      [TallyType.Replays]: talliesStatus.sessionRecordings,
    };
  }, [talliesStatus]);

  const {hasSomeData, hasAllData} = React.useMemo(() => {
    // Determine which tallies to check based on enabled features
    const talliesToCheck = useSessionReplay
      ? [TallyType.Events, TallyType.Users, TallyType.Replays]
      : [TallyType.Events, TallyType.Users];

    const statusValuesToCheck = talliesToCheck.map((tally) => tallyStatus[tally]);
    const checkTrue = (v: boolean) => v === true;
    return {
      hasAllData: statusValuesToCheck.every(checkTrue),
      hasSomeData: statusValuesToCheck.some(checkTrue),
    };
  }, [tallyStatus, useAutocapture, useSessionReplay]);

  const hasSuccess = !isLoading && !hasError && hasAllData;

  const handleRetry = React.useCallback(async () => {
    try {
      trackOnboardingEvent(TrackingEvent.RetryClicked, {
        [TrackingProperty.CurrentStep]: Step.Verify,
      });
      await refetch({resetPolling: true});
    } catch (error) {
      console.error(`Retry failed:`, error);
    }
  }, [refetch]);

  const getStatusText = (): StatusTextConfig => {
    // Case 1: Tallies we care about have all reported data
    if (hasSuccess) {
      return {
        heading: t(`onboarding.verify.status-box.headings.success`),
        description: t(`onboarding.verify.status-box.descriptions.success`),
      };
    }
    // Case 2: There was an error fetching the tallies. Like the server is down or 500 error.
    else if (hasError) {
      return {
        heading: t(`onboarding.verify.status-box.headings.no-connections-found`),
        description: t(`onboarding.verify.status-box.descriptions.apiError`),
      };
    }
    // Case 3: From the tallies we care about, some have reported data, and some have not (e.g. Timed Out. We've been waiting, but no data is coming in)
    else if (hasTimedOut && hasSomeData) {
      return {
        heading: t(`onboarding.verify.status-box.headings.some-connections-found`),
        description: `onboarding.verify.status-box.descriptions.error`,
        docLink: (
          <a
            href='https://docs.mixpanel.com/docs/tracking-methods'
            onClick={() => {
              trackOnboardingEvent(TrackingEvent.LinkClicked, {
                [TrackingProperty.CurrentStep]: Step.Verify,
                [TrackingProperty.LinkSource]: TrackingEvent.VerifiedBoxDocLinkClick,
              });
            }}
            target='_blank'
            rel='noreferrer'
          />
        ),
      };
    }
    // Case 4: From the tallies we care about, none have reported data (e.g. Timed Out. We've been waiting, but no data is coming in)
    else if (hasTimedOut && !hasSomeData) {
      return {
        heading: t(`onboarding.verify.status-box.headings.no-connections-found`),
        description: `onboarding.verify.status-box.descriptions.errors`,
        docLink: (
          <a
            href='https://docs.mixpanel.com/docs/tracking-methods'
            onClick={() => {
              trackOnboardingEvent(TrackingEvent.LinkClicked, {
                [TrackingProperty.CurrentStep]: Step.Verify,
                [TrackingProperty.LinkSource]: TrackingEvent.VerifiedBoxDocLinkClick,
              });
            }}
            target='_blank'
            rel='noreferrer'
          />
        ),
      };
    }
    // Case 5: Haven't timed out, we are still checking. Some tallies we care about have reported data, some have not
    else if (isIdling && hasSomeData) {
      return {
        heading: t(`onboarding.verify.status-box.headings.still-listening-for-data`),
        description: `onboarding.verify.status-box.descriptions.error`,
        docLink: (
          <a
            href='https://docs.mixpanel.com/docs/tracking-methods'
            onClick={() => {
              trackOnboardingEvent(TrackingEvent.LinkClicked, {
                [TrackingProperty.CurrentStep]: Step.Verify,
                [TrackingProperty.LinkSource]: TrackingEvent.VerifiedBoxDocLinkClick,
              });
            }}
            target='_blank'
            rel='noreferrer'
          />
        ),
      };
    }
    return {
      heading: t(`onboarding.verify.status-box.headings.loading`),
    };
  };

  React.useEffect(() => {
    const idlingTimeout = setTimeout(() => {
      setIsIdling(true);
    }, 20000); // Set to 20 seconds

    return () => clearTimeout(idlingTimeout);
  }, []);

  React.useEffect(() => {
    if (hasAllData) {
      setIsIdling(false);
    }
  }, [hasAllData]);

  React.useEffect(() => {
    if (hasError) {
      trackOnboardingEvent(TrackingEvent.ApiEvent, {
        [TrackingProperty.ApiResponse]: TrackingEvent.FailedFetch,
        [TrackingProperty.StepName]: Step.Verify,
      });
    }
    if (hasSomeData) {
      trackOnboardingEvent(TrackingEvent.ApiEvent, {
        [TrackingProperty.ApiResponse]: TrackingEvent.PartialIntegration,
        [TrackingProperty.StepName]: Step.Verify,
        [TrackingProperty.Name]: Object.keys(tallyStatus).toString(),
      });
    }
    if (hasSuccess) {
      trackOnboardingEvent(TrackingEvent.ApiEvent, {
        [TrackingProperty.ApiResponse]: TrackingEvent.SuccessfulIntegration,
        [TrackingProperty.StepName]: Step.Verify,
      });
    }
  }, [hasError, hasSuccess]);

  const statusText = getStatusText();

  return (
    <>
      <div className={clsx(boxStyle.boxContainer, className)}>
        <div className={clsx({[boxStyle.boxHeaderRow]: !hasTimedOut})}>
          {!hasTimedOut && <ICONS.ripple className={clsx({[rippleStyle.loaded]: hasSuccess})} />}
          <div>
            <Typography variant='bodyMd' weight='bold'>
              {statusText.heading}
            </Typography>
            {statusText.description && (
              <Typography variant='captionSm'>
                {statusText.docLink ? (
                  <Trans
                    i18nKey={statusText.description as TranslationKey}
                    t={t}
                    components={{
                      docLink: statusText.docLink,
                    }}
                  />
                ) : (
                  statusText.description
                )}
              </Typography>
            )}
            {hasTimedOut && (
              <button onClick={handleRetry} className={boxStyle.retryButton}>
                <SvgIconReact icon='refresh' />
                <Typography variant='captionSm' weight='bold'>
                  {t(`onboarding.verify.retry-button-text`)}
                </Typography>
              </button>
            )}
          </div>
        </div>
        <div className={boxStyle.boxIconColumn}>
          {Object.entries(tallyStatus).map(([type, status]) => {
            const tallyType = type as TallyType;
            const config = tallyConfig[tallyType];

            // do not show replay icon if session replay is not enabled
            if (tallyType === TallyType.Replays && !useSessionReplay) {
              return null;
            }

            return (
              <TallyIndicator
                key={tallyType}
                icon={config.iconName}
                loaded={status}
                timedOut={hasTimedOut}
                label={config.translationKey as TranslationKey}
              />
            );
          })}
        </div>
      </div>
      {/* either success, or has timed out AND has no user data */}
      {(hasSuccess || hasTimedOut) && !talliesStatus.users && (
        <CalloutBlock
          icon={IMMERSIVE_ASSETS.examineDoc}
          iconAltText={t(`onboarding.implement.no-user-data-callout.heading`)}
          className={common.mt32}
        >
          <Typography className={common.mb4} variant='captionSm' weight='bold'>
            {t(`onboarding.implement.no-user-data-callout.heading`)}
          </Typography>
          <Typography variant='captionXs'>
            <Trans
              i18nKey={`onboarding.implement.no-user-data-callout.description`}
              t={t}
              components={{
                guideLink: <a href='https://docs.mixpanel.com/docs/users' target='_blank' rel='noreferrer' />,
              }}
            />
          </Typography>
        </CalloutBlock>
      )}
    </>
  );
};
